import { BINDS } from './DcsKeymap.js'
import { observedTouch } from '../util/device.js'

/**
 * On-screen controls, for the aeroplanes that are flown on a phone.
 *
 * WHY THIS EXISTS
 * The whole trainer is keyboard-driven, deliberately: every command is a DCS
 * World default binding so the muscle memory transfers, and the one thing the
 * README calls unforgivable is teaching a keystroke that does nothing in the
 * simulator you are training for. A phone has no keyboard, so on a phone the
 * trainer previously could not be used at all — it flew the pattern, told you
 * to lower the gear, and gave you no way to do it.
 *
 * WHAT THIS IS NOT
 * It is not a virtual keyboard, and it is not a joystick. Both would be worse
 * than useless: a tiny QWERTY nobody can hit, or a control the aeroplane does
 * not accept because it is flying itself.
 *
 * WHAT IT IS
 * The trainer already knows exactly what it wants next — that is its entire
 * subject. So the primary control is one large button showing the step you are
 * being asked for, and pressing it does that step. Around it sits a compact pad
 * of the aircraft commands for working ahead or undoing something.
 *
 * EVERY BUTTON CARRIES ITS DCS KEY.
 * That is the part that keeps this a procedures trainer rather than a game. You
 * cannot build keyboard muscle memory on a touchscreen — but you can learn the
 * flow, the order, and what each item is CALLED, and arriving at DCS knowing
 * that the gear is LShift+G is most of the value. So the label is always there,
 * on the big button and on every key in the pad.
 */

/** The pad, in the order a pilot works down it. Grouped as the chart groups. */
const PAD = [
  // `hook` is a toggle, not a pair — LCtrl+H both lowers and raises it, which
  // is what DCS does and therefore what this has to say.
  { bind: 'hook', short: 'HOOK' },
  { bind: 'brakeOut', short: 'BOARDS\nOUT' },
  { bind: 'brakeIn', short: 'BOARDS\nIN' },
  { bind: 'gearDown', short: 'GEAR\nDOWN' },
  { bind: 'gearUp', short: 'GEAR\nUP' },
  { bind: 'flapsDown', short: 'FLAPS\nDOWN' },
  { bind: 'flapsUp', short: 'FLAPS\nUP' },
  { bind: 'dlc', short: 'DLC' },
  { bind: 'autoThrottle', short: 'AUTO\nTHR' },
  { bind: 'sas', short: 'SAS' },
  { bind: 'comms', short: 'CALL\nTHE BALL' },
]

/** Held controls: press and hold, exactly like the keys they stand for. */
const HELD = [
  { bind: 'throttleUp', short: 'THR\n▲' },
  { bind: 'throttleDown', short: 'THR\n▼' },
  { bind: 'wheelBrakes', short: 'WHEEL\nBRAKE' },
]

const VIEWS = [
  { bind: 'view1', short: 'PIT' },
  { bind: 'view2', short: 'CHASE' },
  { bind: 'view3', short: 'ORBIT' },
  { bind: 'view4', short: 'LSO' },
  { bind: 'view5', short: 'PLAT' },
  { bind: 'view6', short: 'TWR' },
]

export class TouchControls {
  /**
   * @param {HTMLElement} container
   * @param {(bindId: string) => void} onPress
   * @param {(bindId: string) => void} onRelease
   */
  constructor(container, onPress, onRelease) {
    this.onPress = onPress
    this.onRelease = onRelease
    this.enabled = false
    this._currentBind = null

    this.root = document.createElement('div')
    this.root.id = 'touch'
    this.root.setAttribute('aria-hidden', 'true')

    // ---- the primary action ---------------------------------------------
    this.primary = document.createElement('button')
    this.primary.id = 'touchdo'
    this.primary.type = 'button'
    this.primary.innerHTML =
      '<span class="lbl">DO THIS</span><span class="ttl">—</span><span class="key">—</span>'
    this._bindButton(this.primary, () => this._currentBind, false)
    this.root.appendChild(this.primary)

    // ---- the drawer -------------------------------------------------------
    // Views and controls in ONE container, spanning the full width along the
    // bottom. As three separately-positioned floating groups they landed on top
    // of the checklist and the jet strip on any phone in landscape, which is
    // every phone this will be used on.
    this.drawer = document.createElement('div')
    this.drawer.id = 'touchdrawer'

    this.views = document.createElement('div')
    this.views.id = 'touchviews'
    for (const item of VIEWS) {
      const b = document.createElement('button')
      b.type = 'button'
      b.className = 'vbtn'
      b.textContent = item.short
      this._bindButton(b, () => item.bind, false)
      this.views.appendChild(b)
    }
    this.drawer.appendChild(this.views)

    this.pad = document.createElement('div')
    this.pad.id = 'touchpad'
    for (const item of PAD) this.pad.appendChild(this._makeKey(item, false))
    for (const item of HELD) this.pad.appendChild(this._makeKey(item, true))
    this.drawer.appendChild(this.pad)

    this.root.appendChild(this.drawer)

    // ---- the drawer handle ------------------------------------------------
    // The pad covers a third of a phone screen, and the whole point of the
    // trainer is watching the aeroplane. So it folds away, and the primary
    // button stays — which is all you need for a clean pass.
    this.toggle = document.createElement('button')
    this.toggle.id = 'touchtoggle'
    this.toggle.type = 'button'
    this.toggle.textContent = 'CONTROLS'
    this.toggle.addEventListener('click', () => {
      const open = this.root.classList.toggle('open')
      this.toggle.classList.toggle('on', open)
      // The reference panels step aside while the drawer is up. On a 390 px
      // tall phone there is not room for both, and when you are reaching for a
      // control you are not reading the checklist.
      document.body.classList.toggle('padopen', open)
    })
    this.root.appendChild(this.toggle)

    container.appendChild(this.root)
  }

  _makeKey({ bind, short }, held) {
    const b = document.createElement('button')
    b.type = 'button'
    b.className = held ? 'kbtn held' : 'kbtn'
    const label = BINDS[bind]?.label ?? '?'
    b.innerHTML =
      `<span class="t">${short.replace(/\n/g, '<br>')}</span>` +
      `<span class="k">${escapeHtml(label)}</span>`
    if (BINDS[bind]?.noDcsDefault) b.classList.add('nodcs')
    this._bindButton(b, () => bind, held)
    return b
  }

  /**
   * Wire one button.
   *
   * Pointer events rather than click, for two reasons. A held control has to
   * know about down and up separately — the throttle is a key you hold, and a
   * click has no duration. And `click` on a touchscreen fires about 300 ms
   * after the finger lands on some browsers, which on a trainer that grades you
   * on when you pressed something is a real error rather than a nicety.
   */
  _bindButton(el, bindOf, held) {
    const down = (e) => {
      e.preventDefault()
      e.stopPropagation()
      const bind = bindOf()
      if (!bind) return
      el.classList.add('down')
      el.setPointerCapture?.(e.pointerId)
      this.onPress(bind)
    }
    const up = (e) => {
      e.preventDefault()
      e.stopPropagation()
      el.classList.remove('down')
      const bind = bindOf()
      if (held && bind) this.onRelease(bind)
    }
    el.addEventListener('pointerdown', down)
    el.addEventListener('pointerup', up)
    el.addEventListener('pointercancel', up)
    // A finger that slides off a held button must release it, or the throttle
    // stays open for the rest of the pattern.
    el.addEventListener('pointerleave', (e) => {
      if (el.classList.contains('down')) up(e)
    })
  }

  /**
   * Turn the controls on the moment a finger touches the screen.
   *
   * The detection in util/device.js is a prediction and predictions are wrong
   * sometimes — `pointer: coarse` is false on Android in "Desktop site" mode
   * and in some in-app webviews. When it is wrong the result is the worst
   * possible failure for this particular app: a phone showing a trainer that
   * asks you to lower the gear, with nothing on screen to do it with and no
   * indication anything is missing.
   *
   * A pointer event that says `touch` is not a guess. So: listen once, and if
   * one ever arrives, switch the controls on regardless of what the device
   * claimed about itself at boot.
   */
  watchForTouch(target) {
    const on = (e) => {
      if (!observedTouch(e) || this.enabled) return
      console.info('[touch] a finger arrived — enabling the on-screen controls')
      this.setEnabled(true)
    }
    // Capture phase, because the buttons themselves stop propagation — and
    // passive, because this only ever reads.
    target.addEventListener('pointerdown', on, { capture: true, passive: true })
    this._watch = () => target.removeEventListener('pointerdown', on, { capture: true })
  }

  /** Show or hide the whole layer. */
  setEnabled(on) {
    this.enabled = on
    this.root.classList.toggle('show', on)
    this.root.setAttribute('aria-hidden', String(!on))
    // A hook for the stylesheet. Some of the layout has to move whenever these
    // controls exist, regardless of screen size — a tablet is wide enough to
    // miss every width query and still needs the big card out of the way of
    // the buttons.
    document.body.classList.toggle('touch', on)
  }

  /**
   * Point the primary button at whatever the trainer is asking for now.
   *
   * @param {object|null} step the snapshot's `current`
   * @param {boolean} holding whether the pattern has stopped to wait
   */
  update(step, holding) {
    // The ACTION is recorded whether or not the layer is showing; only the DOM
    // work is skipped. Bailing out entirely left the primary button holding a
    // stale action from before it was enabled — so a device that switched the
    // controls on late, which is exactly the case `watchForTouch` exists for,
    // got a button that looked correct and pressed nothing until the next
    // frame happened to come round.
    // `bindId` is the identifier; `key` is the label a person reads. The
    // snapshot carries both under deliberately different names, and crossing
    // them gives a button that looks perfect and presses nothing.
    const bind = step?.bindId ?? null
    this._currentBind = bind

    const hasKey = !!bind
    if (!this.enabled) return

    this.primary.classList.toggle('idle', !hasKey)
    this.primary.classList.toggle('holding', !!holding && hasKey)
    this.primary.disabled = !hasKey

    const title = step?.title ?? 'NOTHING TO DO'
    const key = hasKey ? step.key ?? BINDS[bind]?.label ?? '' : 'watch'
    // Only touch the DOM when something actually changed. This runs every
    // frame, and innerHTML on every frame is how a trainer that renders a
    // carrier at 60 fps ends up dropping frames because of a text label.
    if (title !== this._lastTitle || key !== this._lastKey) {
      this.primary.querySelector('.ttl').textContent = title
      this.primary.querySelector('.key').textContent = key
      this._lastTitle = title
      this._lastKey = key
    }
    const lbl = holding ? 'WAITING FOR YOU' : hasKey ? 'DO THIS' : 'ON RAILS'
    if (lbl !== this._lastLbl) {
      this.primary.querySelector('.lbl').textContent = lbl
      this._lastLbl = lbl
    }
  }

  dispose() {
    this._watch?.()
    this.root.remove()
  }
}

function escapeHtml(s) {
  return String(s).replace(/[&<>"]/g, (c) => `&${{ '&': 'amp', '<': 'lt', '>': 'gt', '"': 'quot' }[c]};`)
}

/** Exported so a test can check every button names a real binding. */
export const TOUCH_BINDS = [...PAD, ...HELD, ...VIEWS].map((i) => i.bind)
export { PAD, HELD, VIEWS }
